import { createFileRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { ensurePermissions } from '@/hooks/usePermissions';
import { queryClient } from '@/lib/utils/queryClient';
import { fallback } from '@/lib/utils/zodFallback';
import { OrdersHubPage } from '../-components/OrdersHubPage';
import { ordersHubQueryOptions } from '../-hooks/useOrdersHubList';
import { ORDERS_HUB_DEFAULT_SORT } from '../-utils/listParams';

const ordersHubSearch = z.object({
  tab: z.enum(['orders', 'orderLines']).optional().catch(undefined),
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(100), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
  search: z.string().optional().catch(undefined),
  archived: z.boolean().optional().catch(undefined),
  import: z.boolean().optional().catch(undefined),
});

export const Route = createFileRoute('/(client)/orders-hub/(pages)/')({
  beforeLoad: async () => {
    const { orders } = await ensurePermissions();
    if (!orders.canRead) throw redirect({ to: '/dashboard' });
  },
  validateSearch: ordersHubSearch,
  loaderDeps: ({ search }) => ({
    page: search.page,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
    search: search.search,
    archived: search.archived,
  }),
  loader: ({ deps }) =>
    queryClient.ensureQueryData(
      ordersHubQueryOptions({
        page: deps.page,
        count: deps.limit,
        sort: deps.sort ?? ORDERS_HUB_DEFAULT_SORT,
        ...(deps.search !== undefined ? { search: deps.search } : {}),
        ...(deps.filters !== undefined ? { filters: deps.filters } : {}),
        ...(deps.archived ? { isArchived: true } : {}),
      }),
    ),
  component: OrdersHubPage,
});
